import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { ArrowLeft, Plus, FileDown, Trash2, Edit2, Pencil, Link as LinkIcon, Clock, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate, currency, relativeDue, daysBetween } from "@/lib/utils2";
import AuthImage from "@/components/AuthImage";
import AddMaintenanceDialog from "@/components/AddMaintenanceDialog";
import EditDateButton from "@/components/EditDateButton";
import EditScheduleDialog from "@/components/EditScheduleDialog";
import { toast } from "sonner";

const PLACEHOLDERS = {
  Work: "https://images.unsplash.com/photo-1589746164246-8f32d747c43c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  Home: "https://images.pexels.com/photos/7019609/pexels-photo-7019609.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

export default function EquipmentDetail() {
  const { equipment_id } = useParams();
  const navigate = useNavigate();
  const [eq, setEq] = useState(null);
  const [records, setRecords] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get(`/equipment/${equipment_id}`),
        api.get(`/maintenance`, { params: { equipment_id } }),
        api.get(`/schedule`, { params: { equipment_id } }),
      ]);
      setEq(a.data);
      setRecords(b.data.items);
      setSchedule(c.data.items);
    } catch {
      toast.error("Failed to load equipment");
      navigate("/app/equipment");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [equipment_id]);

  const onDeleteEq = async () => {
    if (!window.confirm("Delete this equipment and all its records?")) return;
    await api.delete(`/equipment/${equipment_id}`);
    toast.success("Deleted");
    navigate("/app/equipment");
  };

  const onSnooze = async (id, days) => {
    await api.post(`/schedule/${id}/snooze`, { days });
    toast.success(`Snoozed ${days} day${days === 1 ? "" : "s"}`);
    load();
  };

  const onComplete = async (id) => {
    await api.post(`/schedule/${id}/complete`);
    toast.success("Marked complete");
    load();
  };

  const onDeleteSchedule = async (id) => {
    await api.delete(`/schedule/${id}`);
    load();
  };

  const onDeleteRecord = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/maintenance/${id}`);
    load();
  };

  const exportEquipmentPDF = () => {
    const url = `${API_BASE}/export/equipment/${equipment_id}`;
    window.open(url, "_blank");
  };

  const exportRecordPDF = (rid) => {
    const url = `${API_BASE}/export/record/${rid}`;
    window.open(url, "_blank");
  };

  if (loading || !eq) {
    return <div className="p-8 text-zinc-400">Loading…</div>;
  }

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1600px] mx-auto">
      <Link to="/app/equipment" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-500 inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to garage
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1 aspect-[4/3] bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
          {eq.photo_ids?.[0] ? (
            <AuthImage fileId={eq.photo_ids[0]} className="w-full h-full object-cover" />
          ) : (
            <img src={PLACEHOLDERS[eq.category] || PLACEHOLDERS.Home} className="w-full h-full object-cover opacity-70" alt="" />
          )}
        </div>
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-2">
            <span className="tag-pill bg-zinc-900 border border-orange-600/50 text-orange-500">{eq.category}</span>
            <span className="tag-pill bg-zinc-900 border border-zinc-700 text-zinc-300">{eq.equipment_type}</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-3" data-testid="equipment-name">{eq.name}</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm mb-6">
            <Stat label="Year" v={eq.year} />
            <Stat label="Color" v={eq.color} />
            <Stat label="Serial" v={eq.serial_number} mono />
            <Stat
              label={eq.usage_unit === "miles" ? "Mileage" : eq.usage_unit === "none" ? "Last serviced" : "Hours"}
              v={eq.usage_unit === "none" ? formatDate(eq.current_hours) : eq.current_hours}
              mono
            />
            <Stat label="Purchased" v={eq.purchase_year} />
            <Stat label="Price" v={currency(eq.purchase_price)} mono />
          </div>
          {eq.special_instructions ? (
            <div className="bg-zinc-900 border-l-2 border-orange-500 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-orange-500 mb-1">// Notes</div>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{eq.special_instructions}</p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={() => setShowAdd(true)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="log-maintenance-btn">
              <Plus className="w-4 h-4 mr-1" /> Log maintenance
            </Button>
            <Button onClick={exportEquipmentPDF} variant="secondary" className="bg-zinc-900 border border-zinc-800 hover:border-orange-600 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="export-equipment-pdf">
              <FileDown className="w-4 h-4 mr-1" /> Export PDF
            </Button>
            <Button onClick={onDeleteEq} variant="ghost" className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="delete-equipment-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Upcoming schedule */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-sm mb-8">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-orange-500" /> Upcoming tasks
          </h2>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{schedule.length} items</div>
        </div>
        {schedule.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No scheduled tasks.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {schedule.map((s) => {
              const d = daysBetween(s.due_date);
              const overdue = d !== null && d < 0;
              return (
                <li key={s.schedule_id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`w-1 h-10 hidden sm:block ${overdue ? "bg-red-500" : d <= 7 ? "bg-orange-500" : "bg-zinc-700"}`} />
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => setEditing(s)}
                      className="font-medium text-left hover:text-orange-500 transition-colors flex items-center gap-2 group"
                      data-testid={`edit-task-${s.schedule_id}`}
                    >
                      {s.task}
                      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-orange-500" />
                    </button>
                    <div className="text-xs text-zinc-500 font-mono mt-0.5 flex items-center gap-2">
                      <span>Every {s.interval} {s.unit}</span>
                      <span className="text-zinc-700">·</span>
                      <EditDateButton
                        scheduleId={s.schedule_id}
                        currentDate={s.due_date}
                        onUpdated={load}
                      >
                        {formatDate(s.due_date)}
                      </EditDateButton>
                      {s.project_id ? <span className="tag-pill bg-zinc-950 border border-orange-600/40 text-orange-500 text-[9px]">in project</span> : null}
                    </div>
                  </div>
                  <div className={`text-sm font-mono ${overdue ? "text-red-400" : d <= 7 ? "text-orange-400" : "text-zinc-300"}`}>
                    {relativeDue(s.due_date)}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => onSnooze(s.schedule_id, 7)} className="text-xs uppercase tracking-widest" data-testid={`snooze-${s.schedule_id}`}>
                      +7d
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onSnooze(s.schedule_id, 30)} className="text-xs uppercase tracking-widest">
                      +30d
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onSnooze(s.schedule_id, 90)} className="text-xs uppercase tracking-widest">
                      +90d
                    </Button>
                    <Button size="sm" onClick={() => onComplete(s.schedule_id)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid={`complete-${s.schedule_id}`}>
                      Done
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDeleteSchedule(s.schedule_id)} className="text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Maintenance history */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h2 className="font-display font-bold text-xl tracking-tight">Maintenance history</h2>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{records.length} records</div>
        </div>
        {records.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No entries yet. Log your first maintenance event above.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {records.map((r) => (
              <li key={r.record_id} className="px-5 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-display font-bold text-lg">{r.task}</h3>
                      {r.cost != null ? <span className="font-mono text-orange-500 text-sm">{currency(r.cost)}</span> : null}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono mb-2">
                      {formatDate(r.performed_at)}{r.hours != null ? ` · ${r.hours} ${eq?.usage_unit === "miles" ? "mi" : "hrs"}` : ""}
                    </div>
                    {r.notes ? <p className="text-zinc-300 text-sm whitespace-pre-wrap mb-2">{r.notes}</p> : null}
                    {r.parts_used ? <p className="text-zinc-400 text-sm mb-2"><span className="text-zinc-500 text-[10px] uppercase tracking-widest mr-2">Parts</span>{r.parts_used}</p> : null}
                    {r.links?.length ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {r.links.map((l, i) => (
                          <a key={`${r.record_id}-link-${i}-${l}`} href={l} target="_blank" rel="noreferrer" className="text-xs text-orange-500 hover:text-orange-400 inline-flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" /> {l.length > 40 ? l.slice(0, 40) + "…" : l}
                          </a>
                        ))}
                      </div>
                    ) : null}
                    {r.photo_ids?.length ? (
                      <div className="flex gap-2 mt-3">
                        {r.photo_ids.slice(0, 6).map((id) => (
                          <AuthImage key={id} fileId={id} className="h-16 w-16 rounded-sm object-cover border border-zinc-800" />
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => exportRecordPDF(r.record_id)} className="text-zinc-400 hover:text-orange-500" data-testid={`export-record-${r.record_id}`}>
                      <FileDown className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDeleteRecord(r.record_id)} className="text-red-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showAdd && (
        <AddMaintenanceDialog
          equipmentId={equipment_id}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <EditScheduleDialog
          schedule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-zinc-200`}>{v ?? "—"}</div>
    </div>
  );
}
